/* global globalThis */
import React, { useEffect, useRef, useState } from "react";
import "./Header.css";
import { HiMiniPencilSquare } from "react-icons/hi2";
import { FaBars, FaQuestionCircle, FaCog, FaChevronDown, FaCheck } from "react-icons/fa";
import FalconLogo from "../../assets/falcon.png";
import CheetahLogo from "../../assets/cheetah.png";
import OwlLogo from "../../assets/owl.png";
import { useMsal } from "@azure/msal-react";
import Select from "react-select";
import axiosClient from "../../api-client/AxiosClient";
import Joyride from "react-joyride";
import PropTypes from "prop-types";
import appConfig from "../../config/appConfig";
import { getUserSelectedOrgConfig } from "./Orgconfigservice";

const application_id = process.env.REACT_APP_APPLICATION_ID;

const Header = ({ isOpen, setIsOpen, run, setRun }) => {
  const getPermissions = () => JSON.parse(sessionStorage.getItem("rolesAndPermission") || "{}");

  // Use state or derive from getPermissions if needed, but for now we get directly from session storage
  // The layout re-renders on 'permissionsUpdated' which handles re-rendering children like Sidebar, 
  // but Header might also need state if its internal rendering depends heavily on these immediately.
  const [permissions, setPermissions] = useState(getPermissions());

  useEffect(() => {
    const handlePermUpdate = () => setPermissions(getPermissions());
    globalThis.addEventListener("permissionsUpdated", handlePermUpdate);
    return () => globalThis.removeEventListener("permissionsUpdated", handlePermUpdate);
  }, []);

  const { Dashboard, DataSource, Upload, STTMapping, Administration } = permissions;

  const { instance } = useMsal();
  const menuRef = useRef(null);
  const [corporationDropdown, setCorporationDropdown] = useState();
  const [corporationList, setCorporationList] = useState([]);
  const [showOrganizationPopup, setShowOrganizationPopup] = useState(false);
  const [showAccessDeniedPopup, setShowAccessDeniedPopup] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [configurationDropdown, setConfigurationDropdown] = useState();
  const [configurationList, setConfigurationList] = useState([]);
  const [showConfigurationPopup, setShowConfigurationPopup] = useState(false);
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);
  const [showNoConfigMessage, setShowNoConfigMessage] = useState(false);
  const [isMandatorySelection, setIsMandatorySelection] = useState(false);

  const [userApplications, setUserApplications] = useState([]);
  const [isAppDropdownOpen, setIsAppDropdownOpen] = useState(false);
  const appDropdownRef = useRef(null);

  // App switcher definitions
  const apps = [
    { name: "Falcon Mapping", id: "falcon", icon: <img src={FalconLogo} className="w-8 h-8 object-contain" alt="Falcon" /> },
    { name: "Code Cheetah", id: "cheetah", icon: <img src={CheetahLogo} className="w-8 h-8 object-contain" alt="Cheetah" /> },
    { name: "Owl Sight", id: "owl", icon: <img src={OwlLogo} className="w-8 h-8 object-contain" alt="Owl" /> }
  ];

  const hasAccessTo = (appId) => {
    return JSON.stringify(userApplications).toLowerCase().includes(appId.toLowerCase());
  };

  const handleAppNavigation = (app) => {
    if (app.name === "Code Cheetah") {
      setIsAppDropdownOpen(false);
    } else if (app.id === "falcon") {
      globalThis.location.href = appConfig.PRODUCT_FALCON_URL;
    } else if (app.id === "owl" && hasAccessTo("owl")) {
      globalThis.location.href = appConfig.PRODUCT_OWL_URL;
    }
  };

  useEffect(() => {
    const fetchApps = async () => {
      try {
        const response = await axiosClient.get("/api/Organization/UserApplications");
        if (response?.data) {
          setUserApplications(response.data);
        }
      } catch (error) {
        console.error("Error fetching user applications:", error);
      }
    };
    fetchApps();
  }, []);



  const [steps, setSteps] = useState([
    {
      target: ".header-icon-joyride-class",
      content:
        "Code Cheetah delivers discipline, intelligence, and simplicity to data migration and reporting, turning a traditionally complex and error-prone process into a streamlined, dependable solution for enterprises.",
      disableBeacon: true,
    },
    {
      target: ".header-corporation-joyride-class",
      content:
        "Seamlessly switch between all organizations you have access to across the platform.",
      disableBeacon: true,
    },
    {
      target: ".header-profile-joyride-class",
      content:
        "Access your account, manage profile settings, and log out across the platform.",
      disableBeacon: true,
    },
  ]);

  useEffect(() => {
    let newSteps = [...steps];

    if (Dashboard?.canView) {
      newSteps.push({
        target: ".sidebar-dashboard-joyride-class",
        content:
          "Track overall mapping progress, review system warnings, and monitor completion status across objects and users.",
        disableBeacon: true,
        placement: "right",
      });
    }
    if (DataSource?.canView) {
      newSteps.push({
        target: ".sidebar-datasource-joyride-class",
        content:
          "Connect to multiple data sources and seamlessly configure, manage, and validate source and target system connections.",
        disableBeacon: true,
        placement: "right",
      });
    }
    if (Upload?.canView) {
      newSteps.push({
        target: ".sidebar-upload-joyride-class",
        content:
          "Upload partially or fully prepared mapping documents for target system objects, with guidance on supported formats and validation.",
        disableBeacon: true,
        placement: "right",
      });
    }
    if (STTMapping?.canView) {
      newSteps.push({
        target: ".sidebar-stt-joyride-class",
        content:
          "Access existing mapping documents to complete, validate, and refine source-to-target field mappings, supported by intelligent suggestions and error highlights.",
        disableBeacon: true,
        placement: "right",
      });
    }
    newSteps.push({
      target: ".sidebar-ai-joyride-class",
      content:
        "Engage with Bluegecko's AI-powered assistant to quickly locate relevant mappings, ask questions, and receive contextual recommendations.",
      disableBeacon: true,
      placement: "right",
    });
    if (Administration?.canView) {
      newSteps.push({
        target: ".sidebar-settings-joyride-class",
        content:
          "Admin Settings provides centralized control over key system configurations. It allows you to manage users and roles, configure organizational settings, and define objects and templates to maintain consistency in mapping and reporting",
        disableBeacon: true,
        placement: "right",
      });
    }

    setSteps(newSteps);
  }, []);

  const toggleDropdown = () => setIsMenuOpen(!isMenuOpen);

  // Helper function to get Select option background color
  const getSelectOptionBackgroundColor = (state) => {
    if (state.isSelected) return "#3B82F6";
    if (state.isFocused) return "#EFF6FF";
    return "white";
  };

  // Helper function to create Select option style
  const createSelectOptionStyle = (provided, state) => {
    return {
      ...provided,
      backgroundColor: getSelectOptionBackgroundColor(state),
      color: state.isSelected ? "white" : "#374151",
      "&:hover": {
        backgroundColor: state.isSelected ? "#3B82F6" : "#EFF6FF",
      },
    };
  };

  // Helper function to format configuration data
  const formatConfigurationData = (data) => {
    return data.map((config) => {
      const templateText = `${config.sourceName} to ${config.targetName}`;
      return {
        label: `${config.domainName} - ${templateText}`,
        value: config.organizationConfigId,
        template: templateText,
        sourceName: config.sourceName,
        targetName: config.targetName,
      };
    });
  };

  // Helper function to handle empty configurations
  const handleEmptyConfigurations = (setShowNoConfigMessage, setConfigurationDropdown) => {
    setShowNoConfigMessage(true);
    setConfigurationDropdown(null);
    localStorage.removeItem("organization_config_id");
    // eslint-disable-next-line no-undef
    globalThis.dispatchEvent(new Event("localStorageUpdated"));
  };

  // Helper function to set stored configuration
  const setStoredConfiguration = (formatted, storedConfigId, setConfigurationDropdown) => {
    const configExists = formatted.find(
      (c) => String(c.value) === String(storedConfigId)
    );
    if (configExists) {
      setConfigurationDropdown(configExists);
      if (configExists.template) {
        localStorage.setItem("template", configExists.template);
      }
      // eslint-disable-next-line no-undef
      globalThis.dispatchEvent(
        new CustomEvent("customStorageChange", {
          detail: {
            key: "organization_config_id",
            newValue: configExists.value,
          },
        })
      );
      return true;
    }
    return false;
  };


  const restoreStoredConfiguration = (formatted) => {
    const storedConfigId = localStorage.getItem("organization_config_id");
    if (storedConfigId) {
      const found = setStoredConfiguration(formatted, storedConfigId, setConfigurationDropdown);
      if (!found) {
        setConfigurationDropdown(null);
        localStorage.removeItem("organization_config_id");
      }
    } else {
      setConfigurationDropdown(null);
    }
  };

  const fetchConfigurations = async (orgId) => {
    try {
      const response = await axiosClient.get(
        `/api/Organization/GetAccessOrganizationConfigs?orgId=${orgId}&applicationId=${application_id}`
      );

      if (!response?.data) {
        setConfigurationList([]);
        setConfigurationDropdown(null);
        setShowNoConfigMessage(true);
        return;
      }

      const data = Array.isArray(response.data) ? response.data : [];
      const formatted = formatConfigurationData(data);

      setConfigurationList(formatted.length > 0
        ? [{ label: "Select Configuration", value: null }, ...formatted]
        : []);

      if (formatted.length === 0) {
        handleEmptyConfigurations(setShowNoConfigMessage, setConfigurationDropdown);
        return;
      }

      setShowNoConfigMessage(false);
      restoreStoredConfiguration(formatted);
    } catch (error) {
      console.error("Error fetching configurations:", error);
      setConfigurationList([]);
      setConfigurationDropdown(null);
      setShowNoConfigMessage(true);
    }
  };


  const handlePopupConfigurationChange = (selected) => {
    if (!selected || selected.value === null) {
      setConfigurationDropdown(null);
      localStorage.removeItem("organization_config_id");
      localStorage.removeItem("template");
      globalThis.dispatchEvent(new Event("localStorageUpdated"));
      globalThis.dispatchEvent(
        new CustomEvent("customStorageChange", {
          detail: {
            key: "organization_config_id",
            newValue: null,
          },
        })
      );
      return;
    }
    setConfigurationDropdown(selected);
    localStorage.setItem("organization_config_id", selected.value);
    if (selected.template) {
      localStorage.setItem("template", selected.template);
    }
    globalThis.dispatchEvent(new Event("localStorageUpdated"));
    globalThis.dispatchEvent(
      new CustomEvent("customStorageChange", {
        detail: {
          key: "organization_config_id",
          newValue: selected.value,
        },
      })
    );

    setShowOrganizationPopup(false);
    setIsMandatorySelection(false); //  Clear mandatory flag after valid selection
  };


  const updateOrganizationSelection = async (selected) => {
    setCorporationDropdown(selected);
    localStorage.setItem("selectedCorporation", JSON.stringify(selected));

    setConfigurationDropdown(null);
    localStorage.removeItem("organization_config_id");
    setShowNoConfigMessage(false);

    await fetchConfigurations(selected.value);

    globalThis.dispatchEvent(new Event("localStorageUpdated"));
    globalThis.dispatchEvent(
      new CustomEvent("customStorageChange", {
        detail: {
          key: "selectedCorporation",
          newValue: JSON.stringify(selected),
        },
      })
    );
  };

  const handleOpenSettings = () => {
    setIsMenuOpen(false);
    setShowSettingsPopup(true);
  };

  const handleSettingsOrgChange = (selected) => {
    updateOrganizationSelection(selected);
  };


  const handleSettingsConfigChange = (selected) => {
    if (!selected || selected.value === null) {
      setConfigurationDropdown(null);
      localStorage.removeItem("organization_config_id");
      localStorage.removeItem("template");
      globalThis.dispatchEvent(new Event("localStorageUpdated"));
      globalThis.dispatchEvent(
        new CustomEvent("customStorageChange", {
          detail: {
            key: "organization_config_id",
            newValue: null,
          },
        })
      );
      return;
    }
    setConfigurationDropdown(selected);
    localStorage.setItem("organization_config_id", selected.value);
    if (selected.template) {
      localStorage.setItem("template", selected.template);
    }
    // eslint-disable-next-line no-undef
    globalThis.dispatchEvent(new Event("localStorageUpdated"));

    // eslint-disable-next-line no-undef
    globalThis.dispatchEvent(
      new CustomEvent("customStorageChange", {
        detail: {
          key: "organization_config_id",
          newValue: selected.value,
        },
      })
    );

    setShowSettingsPopup(false);
  };


  const handleClosePopup = () => {
    setShowOrganizationPopup(false);
  };



  const handleLogout = () => {
    instance.logout().catch((e) => {
      console.error(e);
    });
  };


  const handleEditOrganization = () => {
    setShowOrganizationPopup(true);
  };
  const handlePopupCorporationChange = (selected) => {
    updateOrganizationSelection(selected);
  };

  const handleOutsideClick = (e) => {
    if (isMandatorySelection) return; //  Block closing if selection is mandatory
    if (e.target === e.currentTarget || e.target.classList.contains("popup-overlay")) {
      handleClosePopup();
      setShowConfigurationPopup(false);
      setShowSettingsPopup(false);
    }
  };


  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (
        appDropdownRef.current &&
        !appDropdownRef.current.contains(event.target)
      ) {
        setIsAppDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setLoggedInUser(
      JSON.parse(
        sessionStorage.getItem(
          JSON.parse(sessionStorage.getItem("msal.account.keys"))[0]
        )
      ).name.split(" ")
    );
  }, []);

  const handleMandatorySelection = async (formattedData) => {
    setShowOrganizationPopup(true);
    setIsMandatorySelection(true);

if (formattedData.length > 0) {
      setCorporationDropdown(formattedData[0]);
      localStorage.setItem("selectedCorporation", JSON.stringify(formattedData[0]));
      await fetchConfigurations(formattedData[0].value);
    }
  };

  const handlePreviousSelection = async (data, previousOrgId) => {
    const matchedOrg = data.find((org) => String(org.organizationId) === String(previousOrgId));

    let selectedOrg = null;
    if (matchedOrg) {
      selectedOrg = { label: matchedOrg.name, value: matchedOrg.organizationId };
    } else if (previousOrgId) {
      selectedOrg = { label: "Restoring...", value: previousOrgId };
    }

    if (selectedOrg) {
      setCorporationDropdown(selectedOrg);
      localStorage.setItem("selectedCorporation", JSON.stringify(selectedOrg));
      await fetchConfigurations(selectedOrg.value);
    }

    globalThis.dispatchEvent(new Event("localStorageUpdated"));
  };

  useEffect(() => {
    const fetchCorporations = async () => {
      // Step 1: Fetch orgs the user currently has access to
      const response = await axiosClient.get(
        `/api/Organization/GetAccessOrganizations?applicationId=${application_id}`
      );

      if (!response?.data || response.data.length === 0) {
        setCorporationList([]);
        setShowAccessDeniedPopup(true);
        return;
      }

      const data = Array.isArray(response.data) ? response.data : [];
      const formatted = data.map((corp) => ({
        label: corp.name,
        value: corp.organizationId,
      }));
      setCorporationList(formatted);

      // Step 2: Fetch previously selected org/config from the server
      const previousData = await getUserSelectedOrgConfig();
      const previousOrgId = (
        previousData?.organizationId ||
        previousData?.selectedOrganizationConfig?.organizationId
      )?.toString().trim();
      const previousConfigId = (
        previousData?.organizationConfigId ||
        previousData?.selectedOrganizationConfig?.organizationConfigId
      )?.toString().trim();

      // Step 3: Validate — does the previously selected org still exist in accessible orgs?
      const isOrgStillAccessible = previousOrgId
        ? data.some((org) => String(org.organizationId) === previousOrgId)
        : false;

      if (isOrgStillAccessible) {
        // Restore config ID to localStorage only if the org is still valid
        if (previousConfigId) {
          localStorage.setItem("organization_config_id", previousConfigId);
        }
        await handlePreviousSelection(data, previousOrgId);
      } else {
        // Org is no longer accessible (access revoked) — clear stale data and force selection
        localStorage.removeItem("organization_config_id");
        localStorage.removeItem("selectedCorporation");
        localStorage.removeItem("template");
        await handleMandatorySelection(formatted);
      }
    };
    fetchCorporations();
  }, []);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if (status === "finished" || status === "skipped") {
      setRun(true);
    }
  };

  return (
    <>
      <Joyride
        steps={steps}
        run={!run}
        continuous
        showProgress
        showSkipButton
        disableOverlayClose
        disableScrolling
        styles={{
          options: {
            zIndex: 10000,
          },
          overlay: {
            zIndex: 9999,
          },
          tooltipContainer: {
            zIndex: 10001,
          },
          buttonNext: {
            backgroundImage: "linear-gradient(to right, #3b82f6, #2563eb)",
            color: "#fff",
          },
          buttonBack: {
            color: "#2563eb",
            marginRight: 10,
          },
        }}
        callback={handleJoyrideCallback}
      />
      <header className="header">
        <div className="left-section flex items-center">
          <button className="sidebar-toggle mr-2" onClick={() => setIsOpen(!isOpen)}>
            <FaBars size={18} />
          </button>

          <div className="flex items-center header-icon-joyride-class">
            <img src={CheetahLogo} alt="NextGenlytics Logo" className="logo" />
            <div className="hidden sm:flex items-center ml-3">
              <span className="font-semibold text-gray-900 text-[17px] tracking-tight">BlueGecko</span>
              <span className="mx-2.5 text-gray-300 font-light text-xl">/</span>
            </div>
          </div>
        </div>

        <div className="flex items-center -ml-1 sm:-ml-4 z-10" ref={appDropdownRef}>
          <div className="relative">
            <button
              onClick={() => setIsAppDropdownOpen(!isAppDropdownOpen)}
              className="flex items-center gap-1.5 hover:bg-gray-100/80 px-2.5 py-1.5 rounded-lg text-gray-900 transition-colors focus:outline-none"
            >
              <span className="text-[17px] font-semibold tracking-tight"> Code Cheetah</span>
              <FaChevronDown className={`text-gray-400 text-[10px] mt-0.5 transition-transform duration-200 ${isAppDropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {isAppDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-[280px] bg-white border border-gray-100 rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] z-50 overflow-hidden font-sans origin-top-left animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="px-4 py-3 bg-slate-50/80 border-b border-gray-100">
                  <span className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">Switch Product</span>
                </div>
                <div className="p-2 flex flex-col gap-0.5">
                  {apps.map((app) => {
                    const isCurrent = app.name === "Code Cheetah";
                    const isAccessible = isCurrent || hasAccessTo(app.id);

                    return (
                      <button
                        key={app.name}
                        onClick={() => handleAppNavigation(app)}
                        disabled={!isAccessible}
                        className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center justify-between transition-all duration-200 ${isAccessible ? "hover:bg-slate-50 cursor-pointer" : "opacity-50 cursor-not-allowed bg-slate-50 grayscale"
                          } ${isCurrent ? "bg-blue-50/60 shadow-sm shadow-blue-100/50" : ""}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center shrink-0">
                            {app.icon}
                          </div>
                          <span className={`text-sm ${isCurrent ? "font-bold text-blue-700" : "font-medium text-slate-700"}`}>
                            {app.name}
                          </span>
                        </div>
                        {isCurrent && <FaCheck className="text-blue-600 text-sm" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          className="relative w-full max-w-md mx-auto invisible"
          style={{ fontSize: "14px", color: "#6b7280" }}
        >
          <span className="absolute inset-y-0 left-0 flex items-center pl-3">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1117.5 10a7.5 7.5 0 01-4.85 6.65z"
              />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search objects, modules & mapping progress"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm text-gray-700"
          />
        </div>
        <FaQuestionCircle
          className="text-gray-600 text-lg cursor-pointer mr-4"
          title="Help"
          onClick={() => setRun(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setRun(false);
            }
          }}
        />
        <div className="user-info">
          {/* eslint-disable-next-line no-undef */}
          {globalThis.location.pathname !== "/admin-interface" && (
            <>
              {/* Notification Functionality Removed */}

              {/* Separator */}
              <div className="separator-line mx-3"></div>

{corporationDropdown && (
  <button
    type="button"
    className="organization-hover-container"
    onClick={handleEditOrganization}
    style={{ cursor: "pointer" }}
    aria-label="Edit organization and configuration"
  >
    <span
      className="organization-name header-corporation-joyride-class"
      style={{ fontSize: "14px", color: "#6b7280" }}
    >
      {corporationDropdown.label}
    </span>
    <span className="edit-organization-btn">
      <HiMiniPencilSquare />
    </span>
  </button>
)}




              <div className="separator-line"></div>
            </>
          )}
          <div className="relative" ref={menuRef}>
            {/* Avatar Button */}
            <button
              type="button"
              onClick={toggleDropdown}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-yellow-500 text-white font-bold cursor-pointer header-profile-joyride-class"
              aria-label="Toggle user menu"
            >
              {loggedInUser[0]?.charAt(0)}
              {loggedInUser.length > 1 &&
                loggedInUser.at(-1)?.charAt(0)}
            </button>

            {/* Dropdown */}
            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-auto bg-white rounded-lg shadow-xl border z-50">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <span className="font-semibold text-gray-800">BlueGecko</span>
                  <div className="flex items-center gap-3">
                    <FaCog
                      className="text-gray-600 hover:text-blue-600 cursor-pointer transition-colors"
                      onClick={handleOpenSettings}
                      title="Settings"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleOpenSettings();
                        }
                      }}
                    />
                    <button
                      onClick={handleLogout}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Sign out
                    </button>
                  </div>
                </div>

                {/* Profile Info */}
                <div className="flex items-center gap-[12px] px-4 py-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-yellow-500 text-white font-bold">
                    {loggedInUser[0]?.charAt(0)}
                    {loggedInUser.length > 1 &&
                      loggedInUser.at(-1)?.charAt(0)}
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900">
                      {loggedInUser[0]}{" "}
                      {loggedInUser.length > 1 &&
                        loggedInUser.at(-1)}
                    </span>
                    <span className="text-sm text-gray-600">
                      {JSON.parse(
                        sessionStorage.getItem(
                          JSON.parse(
                            sessionStorage.getItem("msal.account.keys")
                          )[0]
                        )
                      ).username || ""}
                    </span>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </header>

      {/* Access Denied Popup */}
      {showAccessDeniedPopup && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-200/30 backdrop-blur-xl w-full h-full"
          aria-label="Access Denied"
        >
          <div
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '400px', cursor: 'default' }}
            role="presentation"
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center justify-center p-6 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-5">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h3>
              <p className="text-sm text-gray-500 mb-8 font-medium">
                You do not have access to Code Cheetah. Please contact your administrator to request access permissions.
              </p>
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-xl border border-transparent px-5 py-3 bg-blue-600 text-sm font-semibold text-white shadow hover:bg-blue-700 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={(e) => {
                  e.stopPropagation();
                  globalThis.location.href = "/";
                }}
              >
                Go Back to Home
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organization Selection Popup */}
      {/* Unified Selection Popup */}
      {showOrganizationPopup && (
        <button
          type="button"
          className="popup-overlay"
          onClick={handleOutsideClick}
          aria-label="Close popup"
        >
          <div
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              transition: 'all 0.3s ease',
              maxHeight: '500px',
            }}
          >
            <h3 className="popup-title">Select Organization</h3>
            <div className="popup-body">
              <Select
                value={corporationDropdown}
                onChange={handlePopupCorporationChange}
                options={corporationList}
                placeholder="Select Organization"
                isSearchable
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: "40px",
                    borderColor: state.isFocused ? "#3B82F6" : "#D1D5DB",
                    boxShadow: state.isFocused ? "0 0 0 1px #3B82F6" : "none",
                    "&:hover": {
                      borderColor: "#9CA3AF",
                    },
                  }),
                  option: createSelectOptionStyle,
                }}
              />
            </div>

            {/* Configuration Section - Always visible */}
            <div
              className="popup-body"
              style={{
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid #E5E7EB',
              }}
            >
              <h3 className="popup-title" style={{ marginBottom: '10px' }}>
                Select Configuration
              </h3>
              <Select
                value={configurationDropdown}
                onChange={handlePopupConfigurationChange}
                options={configurationList}
                placeholder={corporationDropdown ? "Select Configuration" : "Select Organization first"}
                isSearchable
                isDisabled={!corporationDropdown || configurationList.length === 0}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: "40px",
                    borderColor: state.isFocused ? "#3B82F6" : "#D1D5DB",
                    boxShadow: state.isFocused ? "0 0 0 1px #3B82F6" : "none",
                    backgroundColor: (!corporationDropdown || configurationList.length === 0) ? "#F3F4F6" : "white",
                    cursor: (!corporationDropdown || configurationList.length === 0) ? "not-allowed" : "pointer",
                    "&:hover": {
                      borderColor: (!corporationDropdown || configurationList.length === 0) ? "#D1D5DB" : "#9CA3AF",
                    },
                  }),
                  option: createSelectOptionStyle,
                }}
              />

              {showNoConfigMessage && (
                <div
                  className="mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg"
                  style={{
                    animation: 'fadeIn 0.3s ease',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-amber-600 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <div>
                      <p className="text-sm text-amber-800 font-semibold">
                        No Configuration Available
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        This organization doesn't have any configurations. Please select a different organization.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </button>
      )}


      {showSettingsPopup && (
        <button
          type="button"
          className="popup-overlay"
          onClick={handleOutsideClick}
          aria-label="Close settings popup"
        >
          <div
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '500px' }}
            role="presentation"
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="popup-title" style={{ marginBottom: 0 }}>Settings</h3>
              <button
                onClick={() => setShowSettingsPopup(false)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ×
              </button>
            </div>

            <div className="popup-body">
              {/* Current Organization Section */}
              <div className="mb-6">
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  Current Organization
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900">
                    {corporationDropdown?.label}
                  </p>
                </div>
              </div>

              {/* Change Organization */}
              {corporationList?.length > 1 && (
                <div className="mb-6">
                  <label htmlFor="settings-org-select" className="block text-sm font-medium text-gray-700 mb-2">
                    Change Organization
                  </label>
                  <Select
                    id="settings-org-select"
                    value={corporationDropdown}
                    onChange={handleSettingsOrgChange}
                    options={corporationList}
                    placeholder="Select Organization"
                    isSearchable
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: "40px",
                        borderColor: state.isFocused ? "#3B82F6" : "#D1D5DB",
                        boxShadow: state.isFocused ? "0 0 0 1px #3B82F6" : "none",
                        "&:hover": {
                          borderColor: "#9CA3AF",
                        },
                      }),
                      option: createSelectOptionStyle,
                    }}
                  />
                </div>
              )}

              {/* Current Configuration Section */}
              <div className="mb-6">
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  Current Configuration
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900">
                    {configurationDropdown?.label || "No configuration selected"}
                  </p>
                </div>
              </div>

              {/* Change Configuration */}
              {configurationList?.length > 0 && (
                <div className="mb-4">
                  <label htmlFor="settings-config-select" className="block text-sm font-medium text-gray-700 mb-2">
                    Change Configuration
                  </label>
                  <Select
                    id="settings-config-select"
                    value={configurationDropdown}
                    onChange={handleSettingsConfigChange}
                    options={configurationList}
                    placeholder="Select Configuration"
                    isSearchable
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: "40px",
                        borderColor: state.isFocused ? "#3B82F6" : "#D1D5DB",
                        boxShadow: state.isFocused ? "0 0 0 1px #3B82F6" : "none",
                        "&:hover": {
                          borderColor: "#9CA3AF",
                        },
                      }),
                      option: createSelectOptionStyle,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </button>
      )}


      {/* Configuration Selection Popup */}
      {showConfigurationPopup && (
        <button
          type="button"
          className="popup-overlay"
          onClick={handleOutsideClick}
          aria-label="Close configuration popup"
        >
          <div
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h3 className="popup-title">Select Configuration</h3>
            <div className="popup-body">
              <Select
                value={configurationDropdown}
                onChange={handlePopupConfigurationChange}
                options={configurationList}
                placeholder="Select Configuration"
                isSearchable
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: "40px",
                    borderColor: state.isFocused ? "#3B82F6" : "#D1D5DB",
                    boxShadow: state.isFocused ? "0 0 0 1px #3B82F6" : "none",
                    "&:hover": {
                      borderColor: "#9CA3AF",
                    },
                  }),
                  option: createSelectOptionStyle,
                }}
              />
            </div>
          </div>
        </button>
      )}
    </>
  );
};

Header.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  run: PropTypes.bool.isRequired,
  setRun: PropTypes.func.isRequired,
};

export default Header;
